import type { ProjectScan, ToolingInventoryResult, InventoryItem } from '../../core-open/index.js';

/** Maps package names to display names, grouped by category */
const CATEGORIES: Record<keyof ToolingInventoryResult, Record<string, string>> = {
  frontend: {
    'react': 'React',
    'react-dom': 'React DOM',
    'vue': 'Vue',
    '@angular/core': 'Angular',
    'svelte': 'Svelte',
    'solid-js': 'Solid',
    'qwik': 'Qwik',
    'htmx.org': 'htmx',
    'preact': 'Preact',
    'lit': 'Lit',
    'alpinejs': 'Alpine.js',
    'stimulus': 'Stimulus',
    'petite-vue': 'petite-vue',
    'mithril': 'Mithril',
    'inferno': 'Inferno',
    'hyperapp': 'Hyperapp',
    'marko': 'Marko',
    '@builder.io/qwik': 'Qwik (Builder)',
    'million': 'Million',
    'ember-source': 'Ember',
  },
  metaFrameworks: {
    'next': 'Next.js',
    'nuxt': 'Nuxt',
    '@analogjs/platform': 'Analog',
    'astro': 'Astro',
    '@remix-run/react': 'Remix',
    'gatsby': 'Gatsby',
    '@sveltejs/kit': 'SvelteKit',
    '@tanstack/start': 'TanStack Start',
    '@adonisjs/core': 'AdonisJS',
    '@redwoodjs/core': 'RedwoodJS',
    'blitz': 'Blitz.js',
    '@solidjs/start': 'SolidStart',
    'fresh': 'Fresh (Deno)',
  },
  bundlers: {
    'vite': 'Vite',
    'webpack': 'webpack',
    'rollup': 'Rollup',
    'esbuild': 'esbuild',
    'parcel': 'Parcel',
    'turbo': 'Turbo',
    'tsup': 'tsup',
    'unbuild': 'unbuild',
    '@swc/core': 'SWC',
    'bun': 'Bun',
    'pkg': 'pkg',
    'ncc': 'ncc',
    '@vercel/ncc': 'Vercel ncc',
    'microbundle': 'microbundle',
    'tsc-watch': 'tsc-watch',
    'ts-node': 'ts-node',
    'tsx': 'tsx',
    'jiti': 'jiti',
    '@rspack/core': 'Rspack',
    'farm': 'Farm',
  },
  css: {
    'tailwindcss': 'Tailwind CSS',
    '@mui/material': 'MUI',
    'vuetify': 'Vuetify',
    'bootstrap': 'Bootstrap',
    'styled-components': 'styled-components',
    '@emotion/react': 'Emotion',
    '@chakra-ui/react': 'Chakra UI',
    'sass': 'Sass',
    '@mantine/core': 'Mantine',
    'antd': 'Ant Design',
    '@radix-ui/react-slot': 'Radix UI',
    '@headlessui/react': 'Headless UI',
    'daisyui': 'DaisyUI',
    '@shadcn/ui': 'shadcn/ui',
    'primereact': 'PrimeReact',
    'primevue': 'PrimeVue',
    '@nextui-org/react': 'NextUI',
    '@ariakit/react': 'Ariakit',
    'windstitch': 'Windstitch',
    'vanilla-extract': 'vanilla-extract',
    '@vanilla-extract/css': 'vanilla-extract',
    'linaria': 'Linaria',
    'stylex': 'StyleX',
    '@stylexjs/stylex': 'StyleX',
    'unocss': 'UnoCSS',
    'postcss': 'PostCSS',
    'autoprefixer': 'Autoprefixer',
    'lightningcss': 'Lightning CSS',
    'less': 'Less',
    'stylus': 'Stylus',
    'open-props': 'Open Props',
    '@ark-ui/react': 'Ark UI',
  },
  backend: {
    'express': 'Express',
    'fastify': 'Fastify',
    '@nestjs/core': 'NestJS',
    'hono': 'Hono',
    'koa': 'Koa',
    '@hapi/hapi': 'Hapi',
    'restify': 'Restify',
    'elysia': 'Elysia',
    '@elysiajs/eden': 'Elysia Eden',
    'moleculer': 'Moleculer',
    '@feathersjs/feathers': 'Feathers',
    'sails': 'Sails',
    'micro': 'Micro',
    'polka': 'Polka',
    'h3': 'h3',
    'nitro': 'Nitro',
    '@trpc/server': 'tRPC Server',
    '@trpc/client': 'tRPC Client',
    'middy': 'Middy (Lambda)',
    'serverless': 'Serverless Framework',
    'aws-lambda': 'AWS Lambda',
    '@aws-sdk/client-lambda': 'AWS Lambda SDK',
    '@cloudflare/workers-types': 'Cloudflare Workers',
    'wrangler': 'Wrangler',
  },
  orm: {
    'prisma': 'Prisma',
    '@prisma/client': 'Prisma Client',
    'drizzle-orm': 'Drizzle',
    'typeorm': 'TypeORM',
    'sequelize': 'Sequelize',
    'knex': 'Knex',
    'pg': 'pg (PostgreSQL)',
    'mysql2': 'mysql2',
    'mongodb': 'MongoDB',
    'ioredis': 'ioredis',
    'redis': 'Redis',
    'better-sqlite3': 'better-sqlite3',
    '@mikro-orm/core': 'MikroORM',
    'mongoose': 'Mongoose',
    'mssql': 'mssql',
    'kysely': 'Kysely',
    'objection': 'Objection.js',
    '@planetscale/database': 'PlanetScale',
    '@neondatabase/serverless': 'Neon',
    '@libsql/client': 'libSQL (Turso)',
    '@electric-sql/pglite': 'PGlite',
    'sql.js': 'sql.js',
    'oracledb': 'Oracle DB',
    'cassandra-driver': 'Cassandra',
    'neo4j-driver': 'Neo4j',
    '@upstash/redis': 'Upstash Redis',
    '@upstash/kafka': 'Upstash Kafka',
    'dynamoose': 'Dynamoose',
    'fauna': 'Fauna',
    'faunadb': 'FaunaDB',
    '@clickhouse/client': 'ClickHouse',
    'influx': 'InfluxDB',
    'slonik': 'Slonik',
    'massive': 'Massive.js',
  },
  testing: {
    'vitest': 'Vitest',
    'jest': 'Jest',
    'mocha': 'Mocha',
    '@playwright/test': 'Playwright',
    'cypress': 'Cypress',
    '@testing-library/react': 'Testing Library (React)',
    '@testing-library/vue': 'Testing Library (Vue)',
    '@testing-library/svelte': 'Testing Library (Svelte)',
    '@testing-library/jest-dom': 'Testing Library DOM',
    '@testing-library/user-event': 'Testing Library User Event',
    'chai': 'Chai',
    'ava': 'AVA',
    'tap': 'node-tap',
    'supertest': 'Supertest',
    'storybook': 'Storybook',
    '@storybook/react': 'Storybook (React)',
    '@storybook/vue3': 'Storybook (Vue)',
    'msw': 'Mock Service Worker',
    'nock': 'Nock',
    'sinon': 'Sinon',
    'faker': 'Faker',
    '@faker-js/faker': 'Faker.js',
    'testcontainers': 'Testcontainers',
    'pact': 'Pact',
    '@pact-foundation/pact': 'Pact',
    'k6': 'k6',
    'artillery': 'Artillery',
    'autocannon': 'Autocannon',
    'puppeteer': 'Puppeteer',
    'webdriverio': 'WebdriverIO',
    'nightwatch': 'Nightwatch',
    'detox': 'Detox (Mobile)',
    'jest-image-snapshot': 'Image Snapshot',
    'happy-dom': 'happy-dom',
    'jsdom': 'jsdom',
    'c8': 'c8 (coverage)',
    'nyc': 'nyc (coverage)',
    '@vitest/coverage-v8': 'Vitest Coverage',
  },
  lintFormat: {
    'eslint': 'ESLint',
    'prettier': 'Prettier',
    'stylelint': 'Stylelint',
    '@biomejs/biome': 'Biome',
    'oxlint': 'oxlint',
    'tslint': 'TSLint',
    '@typescript-eslint/parser': 'typescript-eslint',
    'eslint-config-next': 'ESLint Next.js',
    'eslint-config-prettier': 'ESLint Prettier',
    'eslint-plugin-react': 'ESLint React',
    'eslint-plugin-vue': 'ESLint Vue',
    'husky': 'Husky',
    'lint-staged': 'lint-staged',
    'commitlint': 'commitlint',
    '@commitlint/cli': 'commitlint',
    'lefthook': 'Lefthook',
    'cspell': 'CSpell',
    'markdownlint': 'markdownlint',
    'depcheck': 'depcheck',
    'knip': 'Knip',
    'madge': 'Madge',
    'publint': 'publint',
    'arethetypeswrong': 'Are the Types Wrong',
    'sort-package-json': 'sort-package-json',
  },
  apiMessaging: {
    'graphql': 'GraphQL',
    '@grpc/grpc-js': 'gRPC',
    '@trpc/server': 'tRPC',
    '@connectrpc/connect': 'Connect',
    'socket.io': 'Socket.IO',
    'ws': 'WebSocket (ws)',
    '@apollo/server': 'Apollo Server',
    '@apollo/client': 'Apollo Client',
    'urql': 'URQL',
    'graphql-yoga': 'GraphQL Yoga',
    'mercurius': 'Mercurius',
    'type-graphql': 'TypeGraphQL',
    'nexus': 'Nexus',
    'pothos': 'Pothos',
    '@graphql-codegen/cli': 'GraphQL Codegen',
    'kafkajs': 'Kafka.js',
    'amqplib': 'AMQP (RabbitMQ)',
    'bullmq': 'BullMQ',
    'bull': 'Bull',
    'bee-queue': 'Bee Queue',
    'agenda': 'Agenda',
    'pg-boss': 'pg-boss',
    '@temporalio/client': 'Temporal',
    'inngest': 'Inngest',
    'trigger.dev': 'Trigger.dev',
    'nats': 'NATS',
    'mqtt': 'MQTT',
    'zeromq': 'ZeroMQ',
    'sse-channel': 'SSE Channel',
  },
  observability: {
    '@sentry/node': 'Sentry (Node)',
    '@sentry/browser': 'Sentry (Browser)',
    '@sentry/react': 'Sentry (React)',
    '@sentry/nextjs': 'Sentry (Next.js)',
    '@opentelemetry/api': 'OpenTelemetry API',
    '@opentelemetry/sdk-node': 'OpenTelemetry SDK',
    '@opentelemetry/auto-instrumentations-node': 'OpenTelemetry Auto',
    'applicationinsights': 'Application Insights',
    'pino': 'Pino',
    'winston': 'Winston',
    'dd-trace': 'Datadog',
    'newrelic': 'New Relic',
    'bunyan': 'Bunyan',
    '@axiomhq/pino': 'Axiom (Pino)',
    'loglevel': 'loglevel',
    'consola': 'consola',
    '@logtail/node': 'Logtail',
    'elastic-apm-node': 'Elastic APM',
    'prom-client': 'Prometheus Client',
    '@google-cloud/trace-agent': 'GCP Trace',
    '@google-cloud/logging': 'GCP Logging',
    'lightstep-tracer': 'Lightstep',
    'roarr': 'Roarr',
    'cls-hooked': 'CLS Hooked',
    '@baselime/node-opentelemetry': 'Baselime',
    'highlight.run': 'Highlight',
    'posthog-node': 'PostHog',
    '@amplitude/node': 'Amplitude',
    'mixpanel': 'Mixpanel',
    '@segment/analytics-node': 'Segment',
  },
};

export function scanToolingInventory(projects: ProjectScan[]): ToolingInventoryResult {
  const result: ToolingInventoryResult = {
    frontend: [],
    metaFrameworks: [],
    bundlers: [],
    css: [],
    backend: [],
    orm: [],
    testing: [],
    lintFormat: [],
    apiMessaging: [],
    observability: [],
  };

  // Build a map of package -> { version, found } across all projects
  const packageVersions = new Map<string, string | null>();

  for (const project of projects) {
    for (const dep of project.dependencies) {
      if (!packageVersions.has(dep.package)) {
        packageVersions.set(dep.package, dep.resolvedVersion);
      }
    }
  }

  // Match against categories
  for (const [category, packages] of Object.entries(CATEGORIES)) {
    const items: InventoryItem[] = [];
    for (const [pkg, displayName] of Object.entries(packages)) {
      if (packageVersions.has(pkg)) {
        items.push({
          name: displayName,
          package: pkg,
          version: packageVersions.get(pkg) ?? null,
        });
      }
    }
    items.sort((a, b) => a.name.localeCompare(b.name));
    (result as unknown as Record<string, InventoryItem[]>)[category] = items;
  }

  return result;
}
